import {
  Bot,
  Calendar,
  Eye,
  HandHelping,
  OctagonPause,
  Smile,
  Stethoscope,
  UserRound,
} from 'lucide-react'
import React, { type ComponentProps } from 'react'

import { Badge } from 'slab'
import {
  type BrokersEnrollmentEvent,
  type BrokersEnrollmentEventEnrollmentType,
  type BrokersTaskExecutionContext,
  type BrokersTaskExecutionMethod,
} from 'utils/web_api.schemas'

import { ClickableBadgeWrapper } from './ClickableBadgeWrapper'

type IconProp = ComponentProps<typeof Badge>['leftIcon']
type BadgeType = ComponentProps<typeof Badge>['type']

// Coverage type badges
export const CoverageBadge = ({
  coverageType,
  onClick,
}: {
  coverageType: string | null | undefined
  onClick?: (e: React.MouseEvent) => void
}) => {
  if (!coverageType) return <span>-</span>

  switch (coverageType.toLowerCase()) {
    case 'medical':
      return (
        <ClickableBadgeWrapper onClick={onClick}>
          <Badge type='info' leftIcon={Stethoscope}>
            Medical
          </Badge>
        </ClickableBadgeWrapper>
      )
    case 'dental':
      return (
        <ClickableBadgeWrapper onClick={onClick}>
          <Badge type='neutral' leftIcon={Smile}>
            Dental
          </Badge>
        </ClickableBadgeWrapper>
      )
    case 'vision':
      return (
        <ClickableBadgeWrapper onClick={onClick}>
          <Badge type='neutral' leftIcon={Eye}>
            Vision
          </Badge>
        </ClickableBadgeWrapper>
      )
    default:
      return (
        <ClickableBadgeWrapper onClick={onClick}>
          <span>{coverageType}</span>
        </ClickableBadgeWrapper>
      )
  }
}

// Workflow type badges
export const WorkflowTypeBadge = ({
  workflowType,
  onClick,
}: {
  workflowType: string
  onClick?: (e: React.MouseEvent) => void
}) => {
  const getLabel = (type: string) => {
    switch (type) {
      case 'submission':
        return 'Submission'
      case 'renewal':
        return 'Renewal'
      case 'plan_change':
        return 'Plan Change'
      case 'dependent_change':
        return 'Dependent Change'
      case 'plan_change_and_dependent_change':
        return 'Plan & Dependent Change'
      case 'off_thatch':
        return 'Off Thatch'
      case 'cancellation':
        return 'Cancellation'
      case 'offboarding':
        return 'Offboarding'
      default:
        return type
    }
  }

  const getBadgeType = (type: string): BadgeType => {
    switch (type) {
      case 'submission':
        return 'info'
      case 'renewal':
        return 'success'
      case 'plan_change':
      case 'dependent_change':
      case 'plan_change_and_dependent_change':
        return 'caution'
      case 'off_thatch':
      case 'cancellation':
      case 'offboarding':
        return 'critical'
      default:
        return 'neutral'
    }
  }

  return (
    <ClickableBadgeWrapper onClick={onClick}>
      <Badge type={getBadgeType(workflowType)}>
        {getLabel(workflowType)}
      </Badge>
    </ClickableBadgeWrapper>
  )
}

// Enrollment event badges
export const EnrollmentEventBadge = ({
  enrollmentEvent,
  onClick,
}: {
  enrollmentEvent?: BrokersEnrollmentEvent
  onClick?: (e: React.MouseEvent) => void
}) => {
  if (!enrollmentEvent) return <span>-</span>

  const {
    enrollment_type: enrollmentType,
    enrollment_subtype: enrollmentSubtype,
  } = enrollmentEvent

  const getBadgeType = (type?: BrokersEnrollmentEventEnrollmentType): BadgeType => {
    switch (type) {
      case 'ichra_invite':
        return 'info'
      case 'open_enrollment':
        return 'success'
      case 'special_enrollment':
        return 'caution'
      default:
        return 'neutral'
    }
  }

  const getEnrollmentEventTypeDisplay = (type?: string) => {
    switch (type) {
      case 'ichra_invite':
        return 'ICHRA Invite'
      case 'open_enrollment':
        return 'Open Enrollment'
      case 'special_enrollment':
        return 'Special Enrollment'
      default:
        return type
    }
  }

  const getEnrollmentEventSubtypeDisplay = (subtype?: string) => {
    if (!subtype) return null

    switch (subtype) {
      case 'renewal':
        return 'Renewal'
      case 'loss_of_coverage':
        return 'Loss of Coverage'
      case 'relocation':
        return 'Relocation'
      case 'birth_or_adoption':
        return 'Birth or Adoption'
      case 'marriage':
        return 'Marriage'
      case 'divorce_or_legal_separation':
        return 'Divorce or Legal Separation'
      case 'death_of_dependent':
        return 'Death of Dependent'
      case 'cobra_expiration':
        return 'COBRA Expiration'
      case 'loss_of_subsidy_eligibility':
        return 'Loss of Subsidy Eligibility'
      case 'thatch_incident':
        return 'Thatch Incident'
      case 'other':
        return 'Other'
      default:
        // Fallback to titleized version of the subtype
        return subtype
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
    }
  }

  // Build display name: "Type" or "Type (Subtype)" if subtype exists
  const typeName = getEnrollmentEventTypeDisplay(enrollmentType)
  const subtypeName = getEnrollmentEventSubtypeDisplay(enrollmentSubtype)
  const displayName = subtypeName ? `${typeName} (${subtypeName})` : typeName

  return (
    <ClickableBadgeWrapper onClick={onClick}>
      <Badge type={getBadgeType(enrollmentType)} leftIcon={Calendar}>
        {displayName}
      </Badge>
    </ClickableBadgeWrapper>
  )
}

// Execution method badge
export const ExecutionMethodBadge = ({
  method,
  onClick,
}: {
  method: BrokersTaskExecutionMethod
  onClick?: (e: React.MouseEvent) => void
}) => {
  if (!method) return <span>-</span>

  const getLabel = (m: BrokersTaskExecutionMethod) => {
    switch (m) {
      case 'manual':
        return 'Manual'
      case 'automated':
        return 'Automated'
      default:
        return m
    }
  }

  const getBadgeType = (m: BrokersTaskExecutionMethod): BadgeType => {
    switch (m) {
      case 'manual':
        return 'neutral'
      case 'automated':
        return 'info'
      default:
        return 'neutral'
    }
  }

  const getIcon = (m: BrokersTaskExecutionMethod): IconProp => {
    if (m === 'automated') return Bot
    return UserRound
  }

  return (
    <ClickableBadgeWrapper onClick={onClick}>
      <Badge type={getBadgeType(method)} bordered leftIcon={getIcon(method)}>
        {getLabel(method)}
      </Badge>
    </ClickableBadgeWrapper>
  )
}

// Execution context badge
export const ExecutionContextBadge = ({
  context,
}: {
  context: BrokersTaskExecutionContext
}) => {
  // Most tasks are internal/thatch, so we don't need to show a badge for them
  if (context === 'thatch') {
    return null
  }

  return (
    <Badge type='slate' bordered leftIcon={HandHelping}>
      Member
    </Badge>
  )
}

// Hold badge
export const HoldBadge = ({ isOnHold }: {
  isOnHold: boolean
}) => {
  if (!isOnHold) return null

  return (
    <Badge type='critical' bordered leftIcon={OctagonPause}>
      On Hold
    </Badge>
  )
}
